import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, renderSlot as _renderSlot, unref as _unref, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { ref, onUnmounted, computed } from 'vue'
import Popper from 'vue3-popper'
import { useI18n } from 'vue-i18n'

import { logError } from '@/shared/logger'
import { TOOLTIP_CLOSE_DELAY, TOOLTIP_OPEN_DELAY } from '@/shared/config'

// for short time we hide the tooltip so it can reposition for new content
const TOOLTIP_RESET_TIMEOUT = 100
type Props = {
  value?: string
  hoverText?: string
  copyText?: string
  hasHoverText?: boolean
  locked?: boolean
  placement?: string
  offsetDistance?: string
  width: '100%' | 'auto'
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ClipboardCopy',
  props: {
    value: { default: undefined },
    hoverText: { default: '' },
    copyText: { default: '' },
    hasHoverText: { type: Boolean, default: true },
    locked: { type: Boolean, default: false },
    placement: { default: 'bottom' },
    offsetDistance: { default: '12' },
    width: { default: 'auto' }
  },
  setup(__props: any) {

const { t } = useI18n({ useScope: 'global' })

const props = __props

const hoverText = ref(props.hoverText || t('clipboard_copy.hover_text'))
const copyText = ref(props.copyText || t('clipboard_copy.copy_text'))
const isVisible = ref(false)
const tooltipText = ref('')
const openTimeout = ref()
const closeTimeout = ref()
const isInvalid = computed(() => !props.hasHoverText)

const showTooltip = () => {
  isVisible.value = true
}

const hideTooltip = () => {
  isVisible.value = false
}

const saveToClipboard = (value: string) => {
  navigator.clipboard.writeText(value)
}

const setTooltipText = (text: string) => {
  tooltipText.value = text
}

const handleClick = () => {
  if (!navigator.clipboard) {
    return logError(
      `[ClipboardCopy]: Your browser doesn't support navigator.clipboard API`
    )
  }

  if (props.value) {
    clearTimeout(openTimeout.value)
    saveToClipboard(props.value)
    hideTooltip()

    setTimeout(() => {
      setTooltipText(copyText.value)
      showTooltip()
    }, TOOLTIP_RESET_TIMEOUT)

    closeTimeout.value = setTimeout(() => {
      hideTooltip()
    }, TOOLTIP_CLOSE_DELAY)
  }
}

const handleMouseenter = () => {
  if (isInvalid.value) {
    return
  }

  openTimeout.value = setTimeout(() => {
    setTooltipText(hoverText.value)
    showTooltip()
  }, TOOLTIP_OPEN_DELAY)
}

const handleMouseleave = () => {
  if (isInvalid.value) {
    return
  }

  hideTooltip()
  clearTimeout(openTimeout.value)
}

onUnmounted(() => {
  clearTimeout(openTimeout.value)
  clearTimeout(closeTimeout.value)
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(Popper), {
    show: isVisible.value,
    class: _normalizeClass({ 'can-copy': _ctx.value }),
    style: _normalizeStyle({ width: _ctx.width }),
    placement: _ctx.placement as any,
    "offset-distance": _ctx.offsetDistance,
    locked: _ctx.locked,
    "data-testid": "clipboard-copy",
    onClick: _cache[0] || (_cache[0] = ($event: any) => (handleClick())),
    onMouseenter: _cache[1] || (_cache[1] = ($event: any) => (handleMouseenter())),
    onMouseleave: _cache[2] || (_cache[2] = ($event: any) => (handleMouseleave()))
  }, {
    content: _withCtx(() => [
      _createTextVNode(_toDisplayString(tooltipText.value), 1 /* TEXT */)
    ]),
    default: _withCtx(() => [
      _renderSlot(_ctx.$slots, "default")
    ]),
    _: 3 /* FORWARDED */
  }, 8 /* PROPS */, ["show", "class", "style", "placement", "offset-distance", "locked"]))
}
}

})